import { loadProjects } from 'modules/projects/projectsSlice';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { Box, Flex } from '@chakra-ui/react';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled/macro';
import { RootState } from 'store';
import BoxContent from '../BoxContent/BoxContent';
import BoxMain from '../BoxMain/BoxMain';
import ProjectList from './ProjectList/ProjectList';
import PortfolioInfo from './PortfolioInfo/PortfolioInfo';
import { getDistinctSdgs, toTons } from 'utils/project';
import NoDefaultBannerSmall from 'pages/shared/Portfolio/NoDefaultBannerSmall';

interface ProjectPortfolioProps {
  passedRef?: React.RefObject<HTMLDivElement>;
  boxWidth?: BoxWidth;
  animate?: boolean;
  includeBottomMargin?: boolean;
}

interface HoverOverlayProps {
  isHover: boolean;
}

const HoverOverlay = styled(Box)<HoverOverlayProps>`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  border: 1px solid transparent;
  transition: border-color 0.1s;
`;

const ProjectPortfolio: FC<ProjectPortfolioProps> = ({ passedRef, boxWidth = 'md', animate, includeBottomMargin }) => {
  const [isHover, setIsHover] = useState(false);
  const { ready } = useTranslation('projects');
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const { viewableItems: items, portfolioData, loading } = useSelector((state: RootState) => state.projects);
  const portfolioCarbon = useSelector((state: RootState) => state.impact.portfolioCarbon);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(loadProjects());
  }, []);

  const uniqueSdgs = getDistinctSdgs(items);

  return (
    <BoxContent
      boxWidth={boxWidth}
      includeRightMargin
      animate={animate}
      pos="relative"
      id="portfolio-module"
      className="portfolio-module"
      mb={includeBottomMargin ? '30px !important' : ''}
    >
      <Box ref={passedRef} h="100%" maxH={isCorporateTheme ? '690px' : '730px'}>
        <BoxMain ready={ready && !loading} overlayHeight="420px" overlayType="list">
          {items.length && portfolioData ? (
            <Flex w="100%" direction="column">
              <PortfolioInfo
                title={portfolioData?.title}
                description={portfolioData?.description || ''}
                imageUrl={portfolioData?.mainImage?.url}
                sdgs={uniqueSdgs}
                co2={toTons(portfolioCarbon)}
                forceInfoDisplay
                setIsHover={setIsHover}
              />

              <ProjectList
                projects={items.map(p => ({
                  imageUrl: p.imageUrl ?? '',
                  slug: p.slug ?? '',
                  title: p.title ?? '',
                  status: p.status ?? '',
                  country: p.country,
                  city: p.city,
                }))}
                portfolioTheme={portfolioData?.theme ?? ''}
              />
            </Flex>
          ) : (
            <Flex justify="center" alignItems="center" p="60px 0">
              <NoDefaultBannerSmall />
            </Flex>
          )}
        </BoxMain>
        <HoverOverlay as="span" isHover={isHover} borderColor={isHover ? 'primary.500' : 'transparent'} borderRadius="base" />
      </Box>
    </BoxContent>
  );
};

export default ProjectPortfolio;
